// Shared mappers + parser for the server-side deposit "seed" (see App\Services\SiteAPI\DepositSeedProvider).
//
// The seed is the byte-identical payload of the four API calls the deposit page would otherwise
// fetch on mount. These mappers are the SINGLE source of truth for turning the raw JSON into the
// React models — AccountApi's fetch methods use them too, so a seeded init and a fetched init are
// guaranteed to produce identical objects. The seed is an optional optimisation: when it is absent
// or malformed, parseDepositSeed() returns null and the components fall back to fetching.

import CustomDepositBoxValue from '../../../Api/Models/CustomDepositBoxValue';
import DepositBoxValue from '../../../Api/Models/DepositBoxValue';
import PaymentCountry from '../../../Api/Models/PaymentCountry';
import PaymentMethod from '../../../Api/Models/PaymentMethod';
import PaymentMethodChannel from '../../../Api/Models/PaymentMethodChannel';
import UserDetails from '../../../Api/Models/UserDetails';

export interface DepositCountryOption {
    value: string;
    label: string;
}

export interface DepositSeedRaw {
    paymentMethods: any[];
    depositBoxValues: { customFieldValue?: number; depositBoxValues: any[] };
    userDetails: { details: any; links?: any };
    countryList: [Record<string, string>, string];
}

// Mirrors AccountApi.getPaymentMethods() mapping exactly.
export function mapPaymentMethodsResponse(raw: any[]): PaymentMethod[] {
    return raw.map((item) => {
        const method = new PaymentMethod(item);
        method.paymentCountries = (method.paymentCountries || []).map((country) => new PaymentCountry(country));
        if (item.channels) {
            method.channels = item.channels.map((channel: any) => new PaymentMethodChannel(channel));
        }
        return method;
    });
}

// Mirrors AccountApi.getDepositBoxValues() mapping exactly.
export function mapDepositBoxValuesResponse(raw: {
    customFieldValue?: number;
    depositBoxValues: any[];
}): { customFieldValue: CustomDepositBoxValue; depositBoxValues: DepositBoxValue[] } {
    return {
        customFieldValue: CustomDepositBoxValue.create(raw.customFieldValue || 0),
        depositBoxValues: (raw.depositBoxValues || []).map((item) => new DepositBoxValue(item)),
    };
}

// Mirrors AccountApi.getUserDetails() mapping.
export function mapUserDetailsResponse(rawDetails: any): UserDetails {
    return new UserDetails(rawDetails);
}

// Mirrors Deposit.lazy's inline transform of SiteApi.getCountryListRawResponse() → { value, label }[].
export function mapCountryListResponse(raw: [Record<string, string>, string]): DepositCountryOption[] {
    const fetchedCountries = raw?.[0] ?? {};
    return Object.keys(fetchedCountries).map((code) => ({ value: code, label: fetchedCountries[code] }));
}

export function parseDepositSeed(raw?: string | null): DepositSeedRaw | null {
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw);
        const looksValid =
            parsed &&
            Array.isArray(parsed.paymentMethods) &&
            parsed.depositBoxValues &&
            Array.isArray(parsed.depositBoxValues.depositBoxValues) &&
            parsed.userDetails &&
            parsed.userDetails.details &&
            Array.isArray(parsed.countryList);

        return looksValid ? (parsed as DepositSeedRaw) : null;
    } catch {
        return null;
    }
}
